import { Suspense, useCallback, useRef, useState, type ReactNode } from 'react';
import { line, KB_CHECKLISTS_ROUTE, LIST_PAGES, NEW_DOCUMENT_ID, quickCreateTitle, t, type MapsLinkAnswer, type PagedRecord, type StringKey, type SuggestionRow, keySegment } from '@bms2/shared';
import { RadioGroup } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import type { AccountRowAnswer } from '../accounting/ChartOfAccountsPage';
import { ACCOUNTS_PATH } from '../accounting/api';
import { ASSETS_PATH } from '../assets/api';
import { emptyNodeDraft, nodeBody, type NodeDraft } from '../assets/nodeDraft';
import { lazyGroup } from '../../shell/lazyGroup';
import { NodeFormModal } from './lazyNodeFormModal';
import { post, useApi } from './api';
import { EmploymentModal } from './EmploymentModal';
import { NewRecordModal } from './NewRecordModal';
import { SiteModal } from './SiteModal';
import styles from './RecordPage.module.css';

/**
 * 🔨 The three windows that belong to other route groups load when they are first opened, never with the record page
 * (piece 12; decision 10641): the Chart of Accounts' account form, the asset register's node form (`lazyNodeFormModal`) and the Site Types
 * add window. Each is drawn inside its own empty Suspense, so the page behind it stays drawn while the window arrives.
 */
const NewAccountModal = lazyGroup(() => import('../accounting/ChartOfAccountsPage'))('NewAccountModal');
const SiteTypeAddWindow = lazyGroup(() => import('../settings/SiteTypesPage'))('SiteTypeAddWindow');

/** The record types the Quick-Create form of piece 13 adds. */
export type QuickRecord = 'CompanyRecord' | 'PersonRecord' | 'LocationRecord' | 'Deal' | 'Item' | 'Quote';
/** The kinds a field searching several asks between before it opens a form. */
export type ChoosableRecord = 'CompanyRecord' | 'PersonRecord' | 'LocationRecord' | 'Deal' | 'Item' | 'AssetNode';

/**
 * 🔒 What an Add New footer adds (piece 5, T311; decision 0874). One host opens
 * the form each needs, stacked over the window the footer is in, and picks the
 * saved record into the field that asked.
 */
export type AddNewTarget =
  /** `initialValues` fills the form's fields where the caller already knows them: a merchant's name read off a receipt (decision 1188). */
  | { kind: 'record'; recordType: QuickRecord; role?: 'customer' | 'vendor'; initialValues?: Readonly<Record<string, unknown>> }
  /** A person at a named company: the person and employment window locked to it, so the new person is on its roster. */
  | { kind: 'contact'; companyId: string | null; companyName?: string | null; pick: 'person' | 'employment' }
  /** Somebody our own company employs, hired through HR, which has no screen yet. */
  | { kind: 'employee' }
  | { kind: 'site'; companyId?: string | null; companyName?: string | null; fromLink?: MapsLinkAnswer | null }
  | { kind: 'account' }
  | { kind: 'asset' }
  /** 🔒 A site type: the Site Types page's own add window (decision 10500); the field offers it only to a holder of the list's write. */
  | { kind: 'siteType' }
  /** A document raised on its own page with its lines. */
  | { kind: 'document'; documentType: 'SalesOrder' | 'Invoice' | 'Bill' }
  /** A record no screen adds yet. */
  | { kind: 'unbuilt'; what: 'TaxCategory' | 'ChecklistTemplate' }
  /** A field searching several kinds; the picked row's id is `Type:id` where the field's own search answers that way. */
  | { kind: 'choose'; types: readonly ChoosableRecord[]; typedIds: boolean };

export type AddNewAccept = (row: SuggestionRow) => void;

/** A record type as the string keys spell it, in lower camel case. */

export interface AddNew {
  open: (target: AddNewTarget, accept: AddNewAccept) => void;
  /** The stacked form, rendered by the component that owns the footer; nothing while no footer is open. */
  host: ReactNode;
}

/**
 * The Add New host (piece 5, T311; decision 0874). 🔨 Rendered by the
 * component that draws the footer, so a window, a page and a composite each
 * carry their own and none depends on a provider somewhere above it.
 */
export function useAddNew(givenBaseUrl?: string): AddNew {
  // A composite drawn with no API root of its own reads the build's, as the routes hand every page (piece 1).
  const apiBaseUrl = givenBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '';
  const [active, setActive] = useState<{ target: AddNewTarget; accept: AddNewAccept } | null>(null);
  const open = useCallback((target: AddNewTarget, accept: AddNewAccept) => setActive({ target, accept }), []);
  const host = active ? (
    <AddNewForm
      target={active.target}
      apiBaseUrl={apiBaseUrl}
      onCancel={() => setActive(null)}
      onPicked={(row) => {
        const accept = active.accept;
        setActive(null);
        accept(row);
      }}
    />
  ) : null;
  return { open, host };
}

interface FormProps {
  target: AddNewTarget;
  apiBaseUrl: string;
  onCancel: () => void;
  onPicked: AddNewAccept;
}

/** Opens a page in a new tab, so the window the footer is in keeps its draft. */
function openInNewTab(route: string): void {
  if (typeof window !== 'undefined') window.open(route, '_blank', 'noopener');
}

function AddNewForm({ target, apiBaseUrl, onCancel, onPicked }: FormProps) {
  switch (target.kind) {
    case 'record':
      return (
        <NewRecordModal
          open
          recordType={target.recordType as PagedRecord}
          apiBaseUrl={apiBaseUrl}
          {...(target.role || target.initialValues ? { initialValues: { ...(target.initialValues ?? {}), ...(target.role ? { [target.role === 'customer' ? 'isCustomer' : 'isVendor']: true } : {}) } } : {})}
          onCancel={onCancel}
          onCreated={(created) => onPicked({ id: created.id, line1: created.label })}
        />
      );
    case 'contact':
      if (!target.companyId) return <AddNewForm target={{ kind: 'record', recordType: 'PersonRecord' }} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onPicked={onPicked} />;
      return (
        <EmploymentModal
          open
          origin="company"
          companyId={target.companyId}
          companyName={target.companyName ?? null}
          apiBaseUrl={apiBaseUrl}
          onCancel={onCancel}
          onCreated={(created) => onPicked({ id: target.pick === 'person' ? created.personId : created.employmentId, line1: created.personName ?? '' })}
        />
      );
    case 'employee':
      return <EmployeeAddNew apiBaseUrl={apiBaseUrl} onCancel={onCancel} />;
    case 'site':
      return (
        <SiteModal
          open
          companyId={target.companyId ?? null}
          companyName={target.companyName ?? null}
          apiBaseUrl={apiBaseUrl}
          fromLink={target.fromLink ?? null}
          onCancel={onCancel}
          onCreated={(created) => onPicked({ id: created.siteId, line1: created.label ?? '' })}
        />
      );
    case 'siteType':
      return (
        <Suspense fallback={null}>
          <SiteTypeAddWindow apiBaseUrl={apiBaseUrl} onClose={onCancel} onSaved={(saved) => onPicked({ id: saved.id, line1: saved.name })} />
        </Suspense>
      );
    case 'account':
      return <AccountAddNew apiBaseUrl={apiBaseUrl} onCancel={onCancel} onPicked={onPicked} />;
    case 'asset':
      return <AssetAddNew apiBaseUrl={apiBaseUrl} onCancel={onCancel} onPicked={onPicked} />;
    case 'document': {
      const spec = LIST_PAGES[target.documentType];
      return (
        <Modal
          open
          title={quickCreateTitle(spec.singular)}
          onCancel={onCancel}
          dataModal="add-new-document"
          actions={[
            { kind: 'primary', label: line(t('addNew.document.open'), spec.singular), onAct: () => { openInNewTab(`${spec.route}/${NEW_DOCUMENT_ID}`); onCancel(); } },
            { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
          ]}
        >
          <p className={styles.hint} data-add-new-document={target.documentType}>{t('addNew.document.hint')}</p>
        </Modal>
      );
    }
    case 'unbuilt':
      return (
        <Modal
          open
          title={quickCreateTitle(t(`addNew.entity.${keySegment(target.what)}` as StringKey))}
          onCancel={onCancel}
          dataModal="add-new-unbuilt"
          actions={
            // 🔒 A checklist template is written on the Knowledge section's Checklists tab (decision 2281), so its window opens that tab in a new tab, as a document's does, and keeps the draft it came from.
            target.what === 'ChecklistTemplate'
              ? [{ kind: 'primary', label: t('addNew.checklistTemplate.open'), onAct: () => { openInNewTab(KB_CHECKLISTS_ROUTE); onCancel(); } }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]
              // 🔒 Decision 2140: a signpost that opens nothing writes nothing, so it carries one Close (decision 3063).
              : [{ kind: 'close', label: t('modal.close'), onAct: onCancel }]
          }
        >
          <p className={styles.hint} data-add-new-unbuilt={target.what}>{t(`addNew.unbuilt.${keySegment(target.what)}` as StringKey)}</p>
        </Modal>
      );
    case 'choose':
      return <ChooseAddNew types={target.types} typedIds={target.typedIds} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onPicked={onPicked} />;
  }
}

/**
 * 🔒 Somebody our own company employs (decision 0165): hiring is HR's, which
 * has no screen yet, so the footer opens the person form and, once the person
 * is saved, says they are picked once hired and offers their page.
 */
function EmployeeAddNew({ apiBaseUrl, onCancel }: { apiBaseUrl: string; onCancel: () => void }) {
  const [saved, setSaved] = useState<{ id: string; label: string } | null>(null);
  // 🔨 The person form closes itself after its save; that close is the step to the notice, not the end of the footer.
  const created = useRef(false);
  if (!saved) {
    return (
      <NewRecordModal
        open
        recordType="PersonRecord"
        apiBaseUrl={apiBaseUrl}
        onCancel={() => { if (!created.current) onCancel(); }}
        onCreated={(person) => { created.current = true; setSaved(person); }}
      />
    );
  }
  return (
    <Modal
      open
      title={t('addNew.employee.title')}
      onCancel={onCancel}
      dataModal="add-new-employee"
      actions={[
        { kind: 'primary', label: t('addNew.employee.open'), onAct: () => { openInNewTab(`${LIST_PAGES.PersonRecord.route}/${saved.id}`); onCancel(); } },
        { kind: 'cancel', label: t('modal.close'), onAct: onCancel },
      ]}
    >
      <p className={styles.hint} data-add-new-employee={saved.id}>{line(saved.label, t('addNew.employee.notice'))}</p>
    </Modal>
  );
}

/** A general-ledger account: the Chart of Accounts' own form, over the accounts it offers as parents. */
function AccountAddNew({ apiBaseUrl, onCancel, onPicked }: { apiBaseUrl: string; onCancel: () => void; onPicked: AddNewAccept }) {
  const fetched = useApi<{ rows: AccountRowAnswer[] }>(ACCOUNTS_PATH, apiBaseUrl, { cache: true });
  const accounts = fetched.state.kind === 'ready' && Array.isArray(fetched.state.data?.rows) ? fetched.state.data.rows : [];
  return (
    <Suspense fallback={null}>
      <NewAccountModal apiBaseUrl={apiBaseUrl} accounts={accounts} onClose={onCancel} onSaved={(row) => onPicked({ id: row.id, line1: line(row.accountNumber, row.name) })} />
    </Suspense>
  );
}

/** An asset node at the top of a tree: the asset tree's own node form. */
function AssetAddNew({ apiBaseUrl, onCancel, onPicked }: { apiBaseUrl: string; onCancel: () => void; onPicked: AddNewAccept }) {
  const [problem, setProblem] = useState<string | null>(null);
  const save = async (draft: NodeDraft) => {
    const wrote = await post<{ id: string }>(ASSETS_PATH, { ...nodeBody(draft), parentNodeId: null }, apiBaseUrl);
    if (!wrote.ok) return setProblem(wrote.message);
    onPicked({ id: wrote.data.id, line1: draft.name.trim() });
  };
  return (
    <>
      {problem ? <p className={styles.problem} data-add-new-problem="true">{problem}</p> : null}
      <Suspense fallback={null}>
        <NodeFormModal open mode="new" initial={emptyNodeDraft(null)} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onSave={(draft) => void save(draft)} />
      </Suspense>
    </>
  );
}

/** A field searching several kinds: which kind first, then that kind's own form. */
function ChooseAddNew({ types, typedIds, apiBaseUrl, onCancel, onPicked }: { types: readonly ChoosableRecord[]; typedIds: boolean; apiBaseUrl: string; onCancel: () => void; onPicked: AddNewAccept }) {
  const [kind, setKind] = useState<ChoosableRecord>(types[0]!);
  const [chosen, setChosen] = useState<ChoosableRecord | null>(types.length === 1 ? types[0]! : null);
  const pick: AddNewAccept = (row) => onPicked(typedIds && chosen ? { ...row, id: `${chosen}:${row.id}` } : row);
  if (chosen === 'AssetNode') return <AssetAddNew apiBaseUrl={apiBaseUrl} onCancel={onCancel} onPicked={pick} />;
  if (chosen) return <AddNewForm target={{ kind: 'record', recordType: chosen }} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onPicked={pick} />;
  return (
    <Modal
      open
      title={t('addNew.which.title')}
      onCancel={onCancel}
      dataModal="add-new-choose"
      actions={[
        { kind: 'primary', label: t('addNew.which.next'), onAct: () => setChosen(kind) },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <RadioGroup name="add-new-kind" legend={t('addNew.which.legend')} options={types.map((type) => ({ value: type, label: t(`addNew.kind.${keySegment(type)}` as StringKey) }))} value={kind} onChange={(value) => setKind(value as ChoosableRecord)} />
    </Modal>
  );
}
